import { Component, EventEmitter, OnChanges, OnInit, Output, SimpleChanges, computed, inject, input, signal } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    CrmPromoIdsMap,
    EventModel,
    EventOptionGroup,
    EventParticipantImage,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { MarketSubType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterButtonSize } from '@frontend/sports/common/core/feature/scroll-adapter';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageService, ParticipantType } from '@frontend/sports/participant-image/feature';
import { TabBarItem } from '@frontend/sports/web/tab-bar-utils';
import { replace } from 'lodash-es';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionState } from './option-panel-bottom-action.component';
import { OptionGroupPlayerStatisticsService, StatisticsParams } from './player-statistics/option-group-player-statistics.service';
import { PlayerStatisticsService, SeasonStatisticsType } from './player-statistics/player-statistics.service';

@Component({
    template: '',
})
export abstract class PlayerMilestoneBaseOptionGroupComponent implements OnChanges, OnInit {
    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    protected abstract optionGroupPlayerStatisticsService: OptionGroupPlayerStatisticsService;
    tabs: TabBarItem[] = [];
    isOverUnder = false;

    private optionGroupService = inject(OptionGroupService);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private participantImageService = inject(ParticipantImageService);
    protected sitecore = inject(Sitecore);

    private expanded = signal(false);
    optionGroups = signal<OptionGroup[]>([]);
    participantType = ParticipantType;

    event = input.required<EventModel>();
    optionGroup = input.required<EventOptionGroup>();
    crmPromoIdsMap = input.required<CrmPromoIdsMap>();
    tracking = input.required<string>();
    trackingModuleType = input<string>('');
    showMarketHelp = input<boolean>();
    eventHelpText = input<string>();
    sourceOfPick = input<SourceOfPick>(SourceOfPick.Other);
    betDetails = input<string>('');
    optionsLimitOverride = input<number>();
    isShowTabs = input<boolean>(false);
    scrollAdapterButtonSize = input<ScrollAdapterButtonSize>();
    showPlayerStats = input<boolean>(false);

    showMoreData = computed(() => {
        return {
            totalCount: this.optionGroups().length,
            initialVisibleCount: this.threshold(),
            pageSize: this.optionGroups().length,
        };
    });

    isOptionGroupHasTwoOptions = computed(() => {
        return new Set(this.optionGroup().options.map((item) => item.name || '')).size === 2;
    });

    isTeamGroupingEnabled = computed(() =>
        this.optionGroupService.isTeamGroupingEnabled(this.event(), this.optionGroup(), this.marketGroupingConfig.teamGroupingMaxNumberOfLines),
    );

    visibleOptionsLimit = computed(() => {
        return this.isInSheetView || this.expanded() ? this.optionGroups().length : this.threshold();
    });

    isShowHeadshotImage = computed(() => {
        const leagueId = this.event().league?.id;
        return leagueId ? this.participantImageService.showHeadshotImage(leagueId, OptionGroupType.PlayerMilestone) : false;
    });

    private threshold = computed(() => {
        return this.optionsLimitOverride() || this.marketGroupingConfig.playerMilestoneGroupMaxLines || this.optionGroups().length;
    });

    playerStatisticsService = inject(PlayerStatisticsService);
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;
    readonly seasonStatisticsMessage = this.playerStatisticsService.seasonStatisticsMessage;
    readonly SeasonStatisticsType = SeasonStatisticsType;

    get marketType(): OptionGroupType {
        return this.isOverUnder ? OptionGroupType.PlayerMilestoneOverUnder : OptionGroupType.PlayerMilestone;
    }

    @Output() showAllRequested = new EventEmitter<void>();

    ngOnChanges(changes: SimpleChanges): void {
        this.setOptionGroups(this.optionGroup());
        if (
            changes.optionGroup?.previousValue !== changes.optionGroup?.currentValue ||
            changes.event?.previousValue !== changes.event?.currentValue
        ) {
            this.optionGroupPlayerStatisticsService.initializePlayerStatistics(this.event(), this.marketType);
            this.optionGroupPlayerStatisticsService.fetchPlayerStatistics(this.event(), this.optionGroup());
        }
    }

    ngOnInit(): void {
        const optionGroup = this.optionGroup();
        const marketSubType = optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketSubType);
        if (
            this.marketGroupingConfig.enablePlayerMilestoneByMarketSubType &&
            optionGroup.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === MarketType.OverAndUnder &&
            (!marketSubType || marketSubType?.value !== MarketSubType.YesNo)
        )
            return;
        const categoryAttribute = optionGroup.categoryAttribute;
        if (!categoryAttribute || !this.isShowTabs() || this.tabs.length) return;
        const tabName = `${replace(categoryAttribute, ',', '.')}${this.isOverUnder ? '' : '+'}`;
        this.tabs.push({ id: 1, title: tabName, active: true });
    }

    isCrmMarketPromoted(optionGroup: OptionGroup): boolean {
        return (
            !!this.crmPromoIdsMap().markets[optionGroup.optionGroupId] ||
            (!!optionGroup.templateId && !!this.crmPromoIdsMap().templateIds[optionGroup.templateId])
        );
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState }): void {
        this.expanded.set(state !== 'collapsed');
        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event().sport.name,
            this.expanded() ? 'expand' : 'collapse',
            this.event().participants,
            this.optionGroup().name,
            this.optionGroup().name,
        );
    }

    onTeamGroupingChange(teamGrouping: { optionGroup: EventOptionGroup; controllerTabName?: string }): void {
        this.setOptionGroups(teamGrouping.optionGroup);
    }

    getPlayers(optionGroup: OptionGroup): number[] {
        const players: number[] = [];

        if (optionGroup.player1Id) {
            players.push(optionGroup.player1Id);
        }

        return players;
    }

    getPlayerImage(optionGroup: OptionGroup): EventParticipantImage | undefined {
        return optionGroup.options[0]?.image ?? optionGroup.options[1]?.image;
    }

    togglePlayerStatistics(group: OptionGroup): void {
        const params = this.extractParams(group);
        this.optionGroupPlayerStatisticsService.toggleOpen(this.event(), params, this.optionGroup(), this.marketType);
    }

    isPlayerStatisticsOpen(group: OptionGroup): boolean {
        const params = this.extractParams(group);
        return this.optionGroupPlayerStatisticsService.isOpen(params);
    }

    private extractParams(group: OptionGroup): StatisticsParams {
        const optionGroupId = group.optionGroupId?.toString();
        const optionId = group.options[0]?.id?.toString();
        return {
            optionGroupId,
            playerName: group.attribute,
            key: `${group.optionGroupId}_${group.player1Id}`,
            optionId,
        };
    }

    private setOptionGroups(optionGroup: EventOptionGroup): void {
        this.optionGroups.set(
            this.optionGroupService.getOptionGroups(
                optionGroup,
                (option) => option.player1,
                (group) => group.options[0]?.nativePrice.odds.toNumber(),
            ),
        );
    }
}
